import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { router, useGlobalSearchParams } from 'expo-router';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSharedValue } from 'react-native-reanimated';

import { Box } from '@squire/ui';

import { getBarberByIdOrRouteQuery } from '@/api/booking/getBarberByIdOrRouteQuery';
import { getCartQuery } from '@/api/booking/getCartQuery';
import { getServiceCategoriesQuery } from '@/api/booking/getServiceCategoriesQuery';
import { getServicesQuery } from '@/api/booking/getServicesQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { ViewOrderBar } from '@/components/_shared/ViewOrderBar';
import { CategoryUnavailable } from '@/components/booking/CategoryUnavailable';
import { DeleteServiceConfirmationBottomSheet } from '@/components/booking/DeleteServiceConfirmationBottomSheet';
import { ServiceGrid } from '@/components/booking/ServiceGrid/ServiceGrid';
import { ServiceGridSkeleton } from '@/components/booking/ServiceGridSkeleton';
import { ServiceGridSkeletonV2 } from '@/components/booking/ServiceGridSkeletonV2';
import { ServiceListV2 } from '@/components/booking/ServiceListV2';
import { ViewOrderBottomSheet } from '@/components/booking/ViewOrderBottomSheet';
import { routes } from '@/constants/routes';
import { useAddAppointment } from '@/hooks/booking/useAddAppointment';
import {
  isCheckoutStarted,
  useOptimisticCartUpdater,
} from '@/hooks/booking/useOptimisticCartUpdater';
import { useServiceStepTracking } from '@/hooks/booking/useServiceStepTracking';
import { useOnMount } from '@/hooks/shared/useOnMount';
import { useScreenWidth } from '@/hooks/shared/useScreenWidth';
import { useUnfocusCallback } from '@/hooks/shared/useUnfocusCallback';
import type { Cart, Service } from '@/types/booking';
import { assignUncategorizedToOther } from '@/utils/booking/assignUncategorizedToOther';
import { getServiceGridColumns } from '@/utils/booking/getServiceGridColumns';
import { getServiceGroupName } from '@/utils/booking/getServiceGroupName';
import { isServicesStepWebPath } from '@/utils/booking/isServicesStepWebPath';
import { isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';
import { Tracker } from '@/utils/tracking';

import { getScreenServices } from './getScreenServices';
import type { ChooseServiceScreenProps } from './types';
import { useFlatServiceGroups } from './useFlatServiceGroups';
import { useGiftCardEntry } from './useGiftCardEntry';
import { usePreselectService } from './usePreselectService';

export function ChooseServiceScreen({
  shopId: shopRoute,
  cartId,
  barberIdOrRoute,
  promoId: _promoId,
  isAddedAppointment = false,
  groupId,
  categoryId,
  ListComponent,
  isCategoryFirstEnabled,
}: ChooseServiceScreenProps) {
  const params = useGlobalSearchParams();
  const preselectId = Array.isArray(params.preselect)
    ? params.preselect[0]
    : params.preselect;
  const viewOrderBottomSheetRef = useRef<BottomSheetRef>(null);
  const orderSheetIndex = useSharedValue(-1);
  const deleteServiceSheetRef = useRef<BottomSheetRef>(null);
  const [pendingRemovalAddons, setPendingRemovalAddons] = useState<Service[]>(
    [],
  );
  const [pendingRemovalServiceName, setPendingRemovalServiceName] =
    useState('');
  const isListView = ListComponent === ServiceListV2;
  const isFiltered = isCategoryFirstEnabled && !!categoryId;
  const screenWidth = useScreenWidth();

  const [isInitializingCart, setIsInitializingCart] = useState(
    !cartId && !!barberIdOrRoute,
  );

  const { data: shop } = useSuspenseQuery(shopDetailQuery(shopRoute));

  // Fetch scope must match the render scope (`isAnyProfessional` below), which
  // also treats a `bookedWithAnyBarber` cart item as any-barber on a specific
  // route. Both signals (route param + cached cart) are available WITHOUT the
  // barber network response, so services runs in parallel with the barber query
  // below — no waterfall on the common "any professional" path.
  const queryClient = useQueryClient();
  const cachedCart = queryClient.getQueryData<Cart>(
    getCartQuery(shop.id, cartId).queryKey,
  );
  const isAnyBarberBooking =
    barberIdOrRoute === 'any' ||
    !!cachedCart?.items?.at(-1)?.bookedWithAnyBarber;

  // Specific professional: barber-scoped endpoint (SQR-14002 fixes empty
  // service lists for booking-code-gated barbers); "any" keeps shop-level.
  // The barber-scoped services endpoint resolves the barber as a resource
  // owner by id only — a route slug 404s with "Owner can't be found" — so we
  // resolve the barber first and pass its id (matching water-on-web).
  const { data: barberResponse } = useSuspenseQuery(
    getBarberByIdOrRouteQuery(shop.id, barberIdOrRoute, undefined),
  );
  const barberIdForServices = isAnyBarberBooking
    ? undefined
    : barberResponse.barber.id;
  const { data: servicesData } = useSuspenseQuery(
    getServicesQuery(shop.id, barberIdForServices),
  );

  // Same query as the category route's, so it is cached by the time it runs.
  const { data: categoryRows, isLoading: isLoadingCategories } = useQuery({
    ...getServiceCategoriesQuery({
      brandId: shop.brandId,
      shopId: shop.id,
      barberId: barberIdForServices,
    }),
    enabled: isCategoryFirstEnabled,
  });
  const getCategoryName =
    isFiltered && categoryRows
      ? (service: Service) => getServiceGroupName(service, categoryRows.rows)
      : undefined;

  const {
    cart,
    isCartInvalid,
    clearServices,
    addService,
    removeService,
    isMutatingCart,
    isFetchingCart,
    addEmptyCartItem,
    removeLastCartItem,
    isLoadingCart,
    createCartWithProfessional,
    changeService,
  } = useOptimisticCartUpdater(
    shop.id,
    cartId,
    servicesData?.services,
    _promoId,
  );

  useEffect(() => {
    Tracker.setTags({
      shop_id: shop.id,
      brand_id: shop.brandId,
    });
  }, [shop.id, shop.brandId]);

  const currentAppointment = cart?.items.at(-1);
  const selectedServices = useMemo(
    () => currentAppointment?.services || [],
    [currentAppointment],
  );

  // Reuse the same signal that scoped the fetch, OR-ed with the live cart item
  // (which can update after the cached peek). This guarantees the render scope
  // is never "specific" while the fetch was "any-barber" (shop-level) — the
  // combination that would filter a full list as if it were barber-scoped.
  const isAnyProfessional =
    isAnyBarberBooking || !!currentAppointment?.bookedWithAnyBarber;

  const { showGiftCard, handleGiftCardPress } = useGiftCardEntry({
    shop,
    shopRoute,
    promoId: _promoId,
    isAnyProfessional,
    selectedServicesCount: selectedServices.length,
    barberId: currentAppointment?.barberId || barberResponse.barber.id,
  });

  const categorizedServices = useMemo(
    () =>
      isCategoryFirstEnabled && servicesData?.services && categoryRows
        ? assignUncategorizedToOther(servicesData.services, categoryRows.rows)
        : servicesData?.services,
    [isCategoryFirstEnabled, servicesData?.services, categoryRows],
  );

  const services = getScreenServices({
    services: categorizedServices,
    cart,
    isAnyProfessional,
    barberId: currentAppointment?.barberId || barberResponse.barber.id,
    isCategoryFirstEnabled,
  });
  const { isFlat, ...flatListProps } = useFlatServiceGroups({
    isUnfiltered: isCategoryFirstEnabled && !categoryId,
    isFiltered,
    requireBarberAssignment: isAnyProfessional,
    allServices: categorizedServices,
    services,
    rows: categoryRows?.rows,
  });

  const isCategoryUnavailable =
    isFiltered &&
    !services.some((service) =>
      service.categories?.some((category) => category.id === categoryId),
    );

  const [userSelectedCategoryId, setSelectedCategoryId] = useState<string>();
  const selectedCategoryId = isFiltered ? categoryId : userSelectedCategoryId;
  const columns = isListView ? 2 : getServiceGridColumns(screenWidth);

  // Guard so the two create paths (useOnMount + isCartInvalid effect) fire at
  // most once per mount. Prevents duplicate cart creation when the persisted
  // cache transiently resolves null after restore (SQR-12748) — including the
  // self-induced cartId change from our own create. A genuine cart change
  // remounts this screen (route key includes the cart param), giving a fresh
  // guard, so it must NOT reset on cartId change here.
  const hasInitializedCartRef = useRef(false);

  useOnMount(() => {
    if (!cartId && !!barberIdOrRoute && !hasInitializedCartRef.current) {
      Logger.console('Creating cart');
      hasInitializedCartRef.current = true;
      setIsInitializingCart(true);
      createCartWithProfessional(
        barberIdOrRoute === 'any'
          ? { type: 'any' }
          : {
              type: 'specific',
              barberId: barberResponse.barber.id,
            },
      )
        // Release the guard if the create fails so a later attempt can retry —
        // otherwise one transient error blocks all cart creation for this mount.
        .catch(() => {
          hasInitializedCartRef.current = false;
        })
        .finally(() => {
          setIsInitializingCart(false);
        });
    }
  });

  // If the cart ID in the URL is invalid (expired, consumed, or bogus),
  // create a fresh cart so the user can continue booking.
  useEffect(() => {
    if (isCartInvalid && !!barberIdOrRoute && !hasInitializedCartRef.current) {
      Logger.console('Cart is invalid, creating a new cart', { cartId });
      hasInitializedCartRef.current = true;
      setIsInitializingCart(true);
      createCartWithProfessional(
        barberIdOrRoute === 'any'
          ? { type: 'any' }
          : {
              type: 'specific',
              barberId: barberResponse.barber.id,
            },
      )
        // Release the guard if the create fails so a later attempt can retry —
        // otherwise one transient error blocks all cart creation for this mount.
        .catch(() => {
          hasInitializedCartRef.current = false;
        })
        .finally(() => {
          setIsInitializingCart(false);
        });
    }
  }, [
    barberIdOrRoute,
    barberResponse.barber.id,
    cartId,
    createCartWithProfessional,
    isCartInvalid,
  ]);

  const allowMultipleServices =
    barberResponse.barber.allowMultipleServices ?? shop.allowMultipleServices;
  const { trackServiceAdded, trackContinue } = useServiceStepTracking({
    shop,
    barberIdOrRoute,
    groupId,
    categoryId,
    isCategoryFirstEnabled,
    isFlatList: isFlat && !isInitializingCart,
    services,
    categoryRows: categoryRows?.rows,
    selectedServices,
    allowMultipleServices,
  });

  usePreselectService({
    isInitializingCart,
    preselectId,
    services,
    allowMultipleServices,
    barberIdOrRoute,
    addService,
    changeService,
  });

  // Clear services when navigating away, but skip if checkout has started
  // because the cart will no longer exist after successful checkout
  useUnfocusCallback((event) => {
    // Category-first Back keeps the selection; ChooseServiceCategoryScreen and
    // ChooseServiceSubcategoryScreen clear it on Back to the professional step.
    if (isFiltered) return;
    if (isAddedAppointment) {
      if (isCheckoutStarted()) return;
      if (!event && isWeb() && window.location.pathname.endsWith('/schedule')) {
        return;
      }
      removeLastCartItem();
      return;
    }
    if (selectedServices.length > 0 && !isCheckoutStarted()) {
      // On web, browser back/forward navigation unmounts this screen without
      // firing beforeRemove (event is undefined). Skip clearing if the destination
      // is /schedule (forward to ChooseTimeScreen) or this screen's own route
      // (/services or the category route) — services must survive the round-trip.
      if (!event && isWeb()) {
        const pathname = window.location.pathname;
        if (isServicesStepWebPath(pathname)) {
          return;
        }
      }
      Logger.console('Clearing services');
      clearServices();
    }
  });

  const showCart =
    cart?.promo ||
    (cart?.items || []).length > 1 ||
    selectedServices.length > 0;

  const handlePrimaryRemoveWithAddonsModal = useCallback(
    (addonServices: Service[]) => {
      const primaryServiceId = selectedServices.at(0)?.id;
      const primaryService = services.find(
        (s) => s.id === primaryServiceId || s.parentId === primaryServiceId,
      );
      setPendingRemovalServiceName(primaryService?.name ?? '');
      setPendingRemovalAddons(addonServices);
      deleteServiceSheetRef.current?.open();
    },
    [selectedServices, services],
  );

  const handleConfirmRemoval = useCallback(() => {
    clearServices();
    setPendingRemovalAddons([]);
    setPendingRemovalServiceName('');
  }, [clearServices]);

  const handleServiceSelect = async (service: Service) => {
    const serviceIndex = selectedServices.findIndex((s) => s.id === service.id);
    const isServiceAlreadySelected = serviceIndex !== -1;
    const idOrParentId = service.id;

    if (isServiceAlreadySelected) {
      removeService(idOrParentId);
    } else {
      Tracker.track('service_selected', {
        serviceId: idOrParentId,
      });
      trackServiceAdded(idOrParentId);

      const serviceToAdd = {
        ...service,
        id: idOrParentId,
        price: service.cost,
        categoryId: service.categories?.[0]?.id,
      };

      if (allowMultipleServices) {
        addService(barberIdOrRoute, serviceToAdd);
      } else {
        changeService(barberIdOrRoute, serviceToAdd);
      }
    }
  };

  const addAppointment = useAddAppointment({
    shop,
    cartId: cart?.id,
    barberIdOrRoute,
    barberId: barberResponse.barber.id,
    addEmptyCartItem,
  });

  const handleAddAppointment = () => {
    viewOrderBottomSheetRef.current?.close();
    addAppointment();
  };

  const handleCategoryUnavailablePress = () => {
    if (!groupId || !barberIdOrRoute) return;
    router.push(
      routes.chooseServiceSubcategory({
        shopRoute: shop.route,
        barberId: barberIdOrRoute,
        groupId,
        cart: cart?.id,
        promoId: _promoId,
        isAddedAppointment,
      }),
    );
  };

  const listProps = {
    services,
    testID: 'choose-service-grid',
    onServiceSelect: handleServiceSelect,
    onCategoryChange: setSelectedCategoryId,
    selectedCategoryId,
    categories: servicesData?.categories || [],
    selectedServiceId: selectedServices.at(0)?.id,
    additionalServicesIds: selectedServices.slice(1).map((s) => s.id),
    isLoading: isLoadingCart,
    allowMultipleServices,
    currency: (shop.currency || 'USD').toUpperCase(),
    giftCardEnabled: showGiftCard,
    onGiftCardPress: handleGiftCardPress,
  };

  if (isInitializingCart || (isCategoryFirstEnabled && isLoadingCategories)) {
    return isListView ? (
      <ServiceGridSkeletonV2 />
    ) : (
      <ServiceGridSkeleton columns={columns} />
    );
  }

  return (
    <>
      <Box flex>
        {isCategoryUnavailable ? (
          <CategoryUnavailable
            level="l2"
            onPress={handleCategoryUnavailablePress}
          />
        ) : isListView ? (
          <ServiceListV2
            {...listProps}
            onPrimaryRemoveWithAddons={handlePrimaryRemoveWithAddonsModal}
            {...flatListProps}
            getCategoryName={getCategoryName}
            showPriceRangeTooltip={isCategoryFirstEnabled && isAnyProfessional}
          />
        ) : (
          <ServiceGrid
            columns={columns}
            {...listProps}
            onPrimaryRemoveWithAddons={clearServices}
          />
        )}
      </Box>

      {/* View Order Bar - Only show when service is selected */}
      {showCart && (
        <>
          <ViewOrderBar
            isCreatingCart={isMutatingCart || isFetchingCart}
            testID="choose-service-order-bar"
            selectedServicesCount={selectedServices.length}
            cart={cart || null}
            shop={shop}
            sheetIndex={orderSheetIndex}
            onPress={() => viewOrderBottomSheetRef.current?.open()}
          />
          <ViewOrderBottomSheet
            ref={viewOrderBottomSheetRef}
            cart={cart || null}
            shop={shop}
            onAddAppointment={handleAddAppointment}
            onChooseTime={trackContinue}
            animatedIndex={orderSheetIndex}
          />
        </>
      )}
      <DeleteServiceConfirmationBottomSheet
        ref={deleteServiceSheetRef}
        serviceName={pendingRemovalServiceName}
        addonNames={pendingRemovalAddons.map((s) => s.name)}
        onConfirm={handleConfirmRemoval}
        onDismiss={() => {
          setPendingRemovalAddons([]);
          setPendingRemovalServiceName('');
        }}
      />
    </>
  );
}
